import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, RefreshCw, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getNurtureGymCalendar, bookNurtureSlot } from "@/lib/api";

// The gym's lead-consult calendar (routes/nurture_calendar.py), shown while the rep is on a call: today + 6 days in
// the gym's time, Morning / Evening slot chips. Free slots are green (with how many are already booked in them),
// full ones grey. Picking a free slot → "Book this slot" books it in GoHighLevel and records the consult as booked.
const hm = (t) => {
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "pm" : "am";
  return `${((h + 11) % 12) + 1}${m ? `:${String(m).padStart(2, "0")}` : ""} ${ap}`;
};
const dayLabel = (d, i) => {
  if (i === 0) return "Today";
  if (i === 1) return "Tomorrow";
  const dt = new Date(`${d.date}T12:00:00`);
  return `${d.weekday} ${dt.getDate()}`;
};
const longDay = (d) => new Date(`${d.date}T12:00:00`).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });

export default function NurtureCalendar({ lead, callId, onBooked, compact = false }) {
  const facilityId = lead?.facility_id;
  const [cal, setCal] = useState(null);
  const [day, setDay] = useState(0);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState(null);       // {message, booking_url}: book in GoHighLevel instead
  const load = useCallback((refresh = false) => {
    if (!facilityId) return;
    getNurtureGymCalendar(facilityId, refresh).then(setCal).catch((e) => setCal({ error: e?.response?.data?.detail || "Couldn't read the calendar", days: [] }));
  }, [facilityId]);
  useEffect(() => { setCal(null); setPick(null); setDay(0); setRefused(null); load(false); }, [load]);

  const book = async () => {
    if (!pick || !lead) return;
    setBusy(true);
    try {
      const res = await bookNurtureSlot(lead.id, { start: pick.start, calendar_id: pick.calendar_id, call_id: callId || undefined });
      if (res.ok) {
        toast.success(`${res.message}. Saved as Consult booked${res.confirm ? "; the confirmation call is on the list" : ""}.`);
        onBooked?.(res);
      } else if (res.state === "no_scope") {
        setRefused(res);
      } else {
        toast.error(res.message || "GoHighLevel didn't book it");
        setPick(null);
        load(true);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't book the slot");
    } finally {
      setBusy(false);
    }
  };

  if (!facilityId) return null;
  const days = cal?.days || [];
  const d = days[day];
  const chip = (s) => {
    const selected = pick?.start === s.start;
    const free = s.free && !s.past;
    const tone = selected ? "border-rose-500 bg-rose-500/15 text-white"
      : free ? "border-emerald-500/40 bg-emerald-500/[0.06] text-emerald-200 hover:bg-emerald-500/15"
        : "border-[#2a2a2a] bg-[#111] text-zinc-500";
    const title = s.booked ? `Booked: ${s.booked_names.join(", ")}` : free ? "Free" : "Not bookable";
    return (
      <button key={s.start} type="button" disabled={!free || !lead} title={title} onClick={() => setPick(selected ? null : s)}
        data-testid={`slot-${s.time}`}
        className={`rounded-md border px-2 py-1 text-xs tabular-nums ${tone} ${!free ? "cursor-default" : ""}`}>
        {hm(s.time)}
        {s.booked > 0 && <span className={`ml-1 text-[10px] ${free ? "text-amber-300" : "text-zinc-500"}`}>· {s.booked} booked</span>}
        {!free && !s.booked && <span className="ml-1 text-[10px]">· full</span>}
      </button>
    );
  };
  const session = (label, list) => (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      {list.length ? <div className="flex flex-wrap gap-1.5">{list.map(chip)}</div> : <div className="text-[11px] text-zinc-600">No slots</div>}
    </div>
  );

  return (
    <div className="rounded-md border border-[#262626] bg-[#0b0b0b] p-2.5 space-y-2" data-testid="gym-calendar">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-medium text-zinc-300 min-w-0">
          <CalendarDays className="h-3.5 w-3.5 shrink-0 text-sky-300" />
          <span className="truncate">{(cal?.calendars || []).map((c) => c.name).join(", ") || "Consult calendar"}</span>
          {cal?.tz && <span className="text-[10px] text-zinc-500 shrink-0">· {cal.tz.split("/").pop().replace("_", " ")} time</span>}
        </div>
        <button type="button" onClick={() => { setCal(null); load(true); }} className="text-zinc-500 hover:text-zinc-200" aria-label="Read the calendar again">
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>
      {!cal ? <div className="text-xs text-zinc-500">Reading the calendar…</div> : (
        <>
          {cal.error && (
            <div className="text-[11px] text-amber-300/90">
              {cal.error}
              {cal.booking_url && <> · <a href={cal.booking_url} target="_blank" rel="noopener noreferrer" className="underline">open it in GoHighLevel</a></>}
            </div>
          )}
          {cal.events_error && <div className="text-[11px] text-amber-300/80">{cal.events_error} (booked counts not shown)</div>}
          {days.length > 0 && (
            <>
              <div className="flex gap-1 overflow-x-auto pb-0.5" role="tablist" aria-label="Day">
                {days.map((x, i) => (
                  <button key={x.date} type="button" role="tab" aria-selected={day === i} onClick={() => { setDay(i); setPick(null); }}
                    className={`shrink-0 rounded-md border px-2 py-1 text-[11px] leading-tight text-center ${day === i ? "border-sky-500 bg-sky-500/10 text-white" : "border-[#262626] text-zinc-400 hover:text-zinc-200"}`}>
                    <div>{dayLabel(x, i)}</div>
                    <div className={`text-[10px] ${x.free ? "text-emerald-400/80" : "text-zinc-600"}`}>{x.free} free{x.booked ? ` · ${x.booked} bk` : ""}</div>
                  </button>
                ))}
              </div>
              {d && (
                <div className={`space-y-2 ${compact ? "" : "min-h-[72px]"}`}>
                  {session("Morning", d.morning)}
                  {session("Evening", d.evening)}
                </div>
              )}
            </>
          )}
          {refused ? (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/[0.08] px-2.5 py-2 text-xs text-amber-100" data-testid="book-refused">
              {refused.message}
              {refused.booking_url && (
                <a href={refused.booking_url} target="_blank" rel="noopener noreferrer" className="mt-1 flex items-center gap-1 underline text-amber-200">
                  Open the calendar in GoHighLevel <ExternalLink className="h-3 w-3" />
                </a>
              )}
              <div className="mt-1 text-[10px] text-amber-200/70">Then log the outcome as Consult booked as usual.</div>
            </div>
          ) : pick && lead && (
            <div className="flex items-center justify-between gap-2 rounded-md border border-rose-500/30 bg-rose-500/[0.06] px-2.5 py-1.5">
              <span className="text-xs text-zinc-200">{longDay(d)} · {hm(pick.time)}{pick.booked ? ` (${pick.booked} already booked)` : ""}</span>
              <Button size="sm" className="h-7 px-2.5 text-xs" disabled={busy} onClick={book} data-testid="book-slot">
                {busy ? "Booking…" : "Book this slot"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
